import {
  AgentRequest,
  AgentTaskStatus,
  agentTaskStatusToJSON,
  CodeContext,
  CodePlan,
  Command,
  CommandType,
  Diff,
  DocumentInlineComment,
  IntegrationPlatform,
  MigrationDeployInput,
  MigrationRiskReviewFeedback,
  MigrationSelectedEndpoint,
  RepoMetadataRequest
} from '~/protos/agents'
import { DiffReview } from '~/store/editorSlice'
// Remove invalid import - we'll serialize using AgentRequest.toJSON()
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiError, extractApiErrorMessage } from '~/utils/shared'

// Simplified JSON-based task submission payload
export interface TaskSubmitPayload {
  workspaceUuid: string
  request: {
    command_type: number
    command_string?: string
    platform?: number
    to_platform?: number
    images?: Array<{
      image_extension: string
      image_base64_content: string
    }>
    code_context?: {
      conn_type: string
      org_name?: string
      repo_name?: string
      branch_name?: string
      s3_tar_file_path?: string
    }
    multi_repo_code_context?: Array<{
      conn_type: string
      org_name?: string
      repo_name?: string
      branch_name?: string
      s3_tar_file_path?: string
    }>
    auxiliary_code_context?: Array<{
      conn_type: string
      org_name?: string
      repo_name?: string
      branch_name?: string
      s3_tar_file_path?: string
    }>
    runtime_settings?: {
      java_version?: string
      maven_version?: string
      mule_runtime_version?: string
      settings_xml_raw_txt?: string
      settings_security_xml_raw_txt?: string
    }
    old_runtime_settings?: {
      java_version?: string
      maven_version?: string
      mule_runtime_version?: string
    }
    source_output_examples?: Array<{
      source_data?: string
      output_data?: string
      source_data_type?: string
    }>
    mapping_sheet?: Array<{
      id: string
      base64_encoded_xlsx?: string
      csv?: string
    }>
    user_notes?: string
    input_output_notes?: string
    vm_args?: string
    munit_notes?: string
    munit_enable_coverage?: boolean
    munit_num_scenarios?: number
    generate_flow_diag?: boolean
    generate_seq_diag?: boolean
    run_tests?: boolean
    run_deploy?: boolean
    enable_web_search?: boolean
    dwl_to_input_output_example?: {
      command_string?: string
      example_type?: string
    }
    migration_doc_input?: {
      platform: number
      endpoint_desc?: string
    }
    integration_test_config?: {
      flows: Array<{
        flow_id: string
        examples: Array<{
          input_example: string
          output_example: string
        }>
      }>
      enable_comparison_testing: boolean
    }
    context_upload?: {
      instructions: string
      files: Array<{
        file_name: string
        file_s3_path: string
      }>
      directories?: Array<{
        directory_name: string
        directory_s3_path: string
      }>
    }
    execution_mode?: number
    verification_mode?: number
  }
  signal?: AbortSignal
}

// Simplified task update payload - JSON instead of protobuf
export interface TaskUpdatePayload {
  workspaceUuid: string
  uuid: string
  taskId: number
  status: AgentTaskStatus
  // Optional fields that can be included in the update
  request?: {
    diff_comments?: object[]
    general_diff_comment?: string
    document_inline_comments?: object[]
    diff?: object[]
    command?: {
      command_type?: number
      command_string?: string
    }
    refactor_start_user_comments?: {
      general_comments?: string
      skip_prc_api?: boolean
    }
    selected_endpoints?: object[]
  }
  signal?: AbortSignal
}

// Task update API response
export interface TaskUpdateResponse {
  status: string
  task_id?: number
  new_status?: string
  message?: string
}

/**
 * Submit a task with JSON payload.
 * Backend creates new conversation and handles all protobuf conversion.
 * Simple architecture: Frontend sends JSON → Backend builds protobuf
 */
export async function taskSubmit(payload: TaskSubmitPayload) {
  const { workspaceUuid, request, signal } = payload

  // Submit task - backend creates conversation and handles all protobuf conversion
  const taskSubmitApiPath = `/api/workspace/${workspaceUuid}/conversation/task-submit`

  try {
    const response = await fetchWrapper(taskSubmitApiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal
    })

    if (!response.ok) {
      let parsed = {
        message: `Failed to submit task: ${response.statusText}`,
        errorType: undefined as string | undefined
      }
      try {
        const errorJson = await response.json()
        parsed = extractApiError(errorJson, parsed.message)
      } catch {
        // Fallback to statusText if we can't parse the error response
      }
      const error = new Error(parsed.message) as Error & { errorType?: string }
      error.errorType = parsed.errorType
      throw error
    }

    return response.json() as unknown as {
      taskId: number
      createdTime: number
      conversationUuid: string // Backend returns the created conversation UUID
    }
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('Task submission aborted')
      return
    }
    throw error
  }
}

// Function to submit task updates for INIT tasks (uses JSON)
export async function submitTaskUpdate({
  workspaceUuid,
  uuid,
  taskId,
  request
}: TaskUpdatePayload): Promise<TaskUpdateResponse> {
  try {
    const response = await fetchWrapper(
      `/api/workspace/${workspaceUuid}/conversation/${uuid}/task/${taskId}/submit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          request: request || {},
          task_id: taskId
        })
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error submitting task update:', error)
    throw error
  }
}

/**
 * Update task status with JSON payload.
 * Simple architecture: Frontend sends JSON → Backend builds protobuf
 */
export const updateTaskStatus = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  status: AgentTaskStatus
  ar?: AgentRequest
}) => {
  const { workspaceUuid, uuid, taskId } = payload
  const status = agentTaskStatusToJSON(payload.status)
  const path = `/api/workspace/${workspaceUuid}/conversation/${uuid}/task/${taskId}/task-update/${status}`

  // Convert AgentRequest to simplified JSON if provided
  const requestData: Record<string, unknown> = {}
  if (payload.ar) {
    const ar = payload.ar
    if (ar.diffComments && ar.diffComments.length > 0) {
      requestData.diff_comments = ar.diffComments
    }
    if (ar.generalDiffComment) {
      requestData.general_diff_comment = ar.generalDiffComment
    }
    if (ar.documentInlineComments && ar.documentInlineComments.length > 0) {
      requestData.document_inline_comments = ar.documentInlineComments
    }
    if (ar.diff && ar.diff.length > 0) {
      requestData.diff = ar.diff
    }
    if (ar.command) {
      requestData.command = {
        command_type: ar.command.commandType,
        command_string: ar.command.commandString
      }
    }
    if (ar.refactorStartUserComments) {
      requestData.refactor_start_user_comments = {
        general_comments: ar.refactorStartUserComments.generalComments,
        skip_prc_api: ar.refactorStartUserComments.skipPrcApi
      }
    }
    if (ar.selectedEndpoints && ar.selectedEndpoints.length > 0) {
      requestData.selected_endpoints = ar.selectedEndpoints
    }
    if (ar.migrationRiskReviewFeedback) {
      const review = ar.migrationRiskReviewFeedback
      requestData.migration_risk_review_feedback = {
        feedback: review.feedback,
        uploaded_files: review.uploadedFiles.map(f => ({
          file_name: f.fileName,
          file_s3_path: f.fileS3Path
        }))
      }
    }
    if (ar.migrationDeployInput) {
      const di = ar.migrationDeployInput
      requestData.migration_deploy_input = {
        request_id: di.requestId,
        value: di.value,
        approved: di.approved
      }
    }
  }

  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
  })

  if (!response.ok) {
    const reason = await response.json()
    throw new Error(
      `Failed to update task status. ${extractApiErrorMessage(reason, 'Failed to update task status')}`
    )
  }

  return response.json()
}

export const approvePlan = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  codePlan?: CodePlan
}) => {
  const { workspaceUuid, uuid, taskId, codePlan } = payload
  const ar = AgentRequest.fromPartial({ codePlan })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.PLANNING_UPDATED,
    ar
  })
}

export const regeneratePlan = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
}) => {
  const { workspaceUuid, uuid, taskId } = payload
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.PLANNING_REREQUESTED
  })
}

export const regenerateDocWithComments = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  comments: DocumentInlineComment[]
}) => {
  const { workspaceUuid, uuid, taskId, comments } = payload
  const ar = AgentRequest.fromPartial({ documentInlineComments: comments })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.DOCUMENT_UPDATED,
    ar
  })
}

export const regenerateDoc = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  comment: DocumentInlineComment
}) => {
  const { workspaceUuid, uuid, taskId, comment } = payload
  const ar = AgentRequest.fromPartial({ documentInlineComments: [comment] })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.DOCUMENT_UPDATED,
    ar
  })
}

export const regenerateImage = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  comment: string
}) => {
  const { workspaceUuid, uuid, taskId, comment } = payload
  const ar = AgentRequest.fromPartial({
    command: Command.fromPartial({
      commandType: CommandType.GENERATE_IMAGE,
      commandString: comment
    })
  })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.GENERAL_TXT_FEEDBACK_PROVIDED,
    ar
  })
}

/**
 * Submit the user's whole-review risk feedback for the migration
 * risk-review pause. The user reviews ALL risks together and submits ONE
 * consolidated note (text + optional uploaded files). Pass `undefined` (or
 * an empty feedback with no uploads) to "continue without feedback" — the
 * agent still treats this as the single feedback round being consumed.
 */
export const submitRiskReview = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  feedback?: MigrationRiskReviewFeedback
}) => {
  const { workspaceUuid, uuid, taskId, feedback } = payload
  const ar = AgentRequest.fromPartial({
    migrationRiskReviewFeedback: feedback ?? MigrationRiskReviewFeedback.fromPartial({})
  })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.MIGRATION_RISKS_REVIEWED,
    ar
  })
}

/**
 * Submits the user's answer to a deploy input request during the
 * MIGRATION_AWAITING_DEPLOY_INPUT pause. For a text prompt pass `value`; for a
 * confirm prompt pass `approved`. `requestId` echoes the request being answered.
 */
export const submitDeployInput = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  deployInput: MigrationDeployInput
}) => {
  const { workspaceUuid, uuid, taskId, deployInput } = payload
  const ar = AgentRequest.fromPartial({ migrationDeployInput: deployInput })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.MIGRATION_DEPLOY_INPUT_PROVIDED,
    ar
  })
}

// TODO: comment apis
export const applyDiff = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  diffs: Diff[]
  commandType?: CommandType
}) => {
  const { workspaceUuid, uuid, taskId, diffs } = payload

  const ar = AgentRequest.fromPartial({ diff: diffs })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.HUMAN_DIFF_UPDATED,
    ar
  })
}

export const requestChanges = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  diffReview: DiffReview
  commandType?: CommandType
}) => {
  const { workspaceUuid, uuid, taskId, diffReview } = payload
  const { generalDiffComment, diffComments } = diffReview

  /* See note above – validation moved to React layer to avoid invalid hook call */

  const ar = AgentRequest.fromPartial({ diffComments, generalDiffComment })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.DIFF_COMMENT_ADDED,
    ar
  })
}

export const stopTask = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
}) => {
  await updateTaskStatus({ ...payload, status: AgentTaskStatus.STOPPED })
}

export const getFlowNamesCampaign = async (codeContext: CodeContext) => {
  const path = `/api/conversation/flow-names-test-drive`
  const rmr = RepoMetadataRequest.fromPartial({ codeContext: codeContext })
  try {
    const response = await fetchWrapper(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(RepoMetadataRequest.toJSON(rmr))
    })
    if (!response.ok) {
      const reason = await response.text()
      throw new Error(`Failed to get flow names. ${reason}`)
    }
    const flowNames = await response.json()
    return flowNames
  } catch (err) {
    console.error('getFlowNamesCampaign', err)
    return null
  }
}

export const getRepoMetadata = async (
  workspaceUuid: string,
  codeContext: CodeContext,
  platform?: IntegrationPlatform,
  commandType?: number
) => {
  const path = `/api/workspace/${workspaceUuid}/conversation/repo-metadata`
  const data: Record<string, unknown> = { codeContext }
  if (platform !== undefined) {
    data.platform = platform
  }
  if (commandType !== undefined) {
    data.commandType = commandType
  }

  const t0 = performance.now()
  try {
    const response = await fetchWrapper(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    if (!response.ok) {
      const reason = await response.text()
      throw new Error(`Failed to get repo metadata. ${reason}`)
    }
    const result = await response.json()
    const elapsed = Math.round(performance.now() - t0)
    console.log(`[PERF] getRepoMetadata: completed in ${elapsed}ms`)
    return result // Returns { flows: {...}, suggestedVmArgs: "..." }
  } catch (err) {
    const elapsed = Math.round(performance.now() - t0)
    console.error(`getRepoMetadata failed after ${elapsed}ms`, err)
    return null
  }
}

/**
 * Lightweight flow-name extraction that reads Mule XML files directly
 * from GitHub instead of going through the agent service.
 * Falls back to the agent gRPC call for S3 uploads and non-GitHub connections.
 */
export const getFlowNames = async (
  workspaceUuid: string,
  codeContext: CodeContext
): Promise<{ flowNames: string[] } | null> => {
  const path = `/api/workspace/${workspaceUuid}/conversation/repo-metadata/flow-names`
  const t0 = performance.now()
  try {
    const response = await fetchWrapper(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codeContext })
    })
    if (!response.ok) {
      const reason = await response.text()
      throw new Error(`Failed to get flow names. ${reason}`)
    }
    const result = await response.json()
    const elapsed = Math.round(performance.now() - t0)
    console.log(`[PERF] getFlowNames: ${result?.flowNames?.length ?? 0} flows in ${elapsed}ms`)
    return result
  } catch (err) {
    const elapsed = Math.round(performance.now() - t0)
    console.error(`getFlowNames failed after ${elapsed}ms`, err)
    return null
  }
}

export const submitMigrationEndpoints = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  selectedEndpoints: MigrationSelectedEndpoint[]
}) => {
  const { workspaceUuid, uuid, taskId, selectedEndpoints } = payload
  const ar = AgentRequest.fromPartial({ selectedEndpoints })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
    ar
  })
}

export const submitRefactorUserComments = async (
  workspaceUuid: string,
  uuid: string,
  taskId: number,
  payload: {
    generalComments: string
    skipPrcApi?: boolean
  }
) => {
  const ar = AgentRequest.fromPartial({
    refactorStartUserComments: {
      generalComments: payload.generalComments,
      skipPrcApi: payload.skipPrcApi ?? false
    }
  })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.REFACTOR_USER_NOTES_ADDED,
    ar
  })
}

export const submitQuestionAnswer = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
  answer: string
}) => {
  const { workspaceUuid, uuid, taskId, answer } = payload
  const ar = AgentRequest.fromPartial({
    generalDiffComment: answer
  })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.QUESTION_ANSWERED,
    ar
  })
}

export const submitRefactorFeedback = async (
  workspaceUuid: string,
  uuid: string,
  taskId: number,
  feedback: string
) => {
  const ar = AgentRequest.fromPartial({
    refactorStartUserComments: {
      generalComments: feedback,
      skipPrcApi: false
    }
  })
  return updateTaskStatus({
    workspaceUuid,
    uuid,
    taskId,
    status: AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED,
    ar
  })
}

/**
 * Cancel a running task
 */
export const cancelTask = async (payload: {
  workspaceUuid: string
  uuid: string
  taskId: number
}) => {
  const { workspaceUuid, uuid, taskId } = payload
  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/conversation/${uuid}/task/${taskId}/cancel`,
    { method: 'POST' }
  )
  if (!response.ok) throw new Error('Failed to cancel task')
  return response.json()
}

/**
 * Delete a task using soft delete
 */
export const deleteTask = async (
  workspaceUuid: string,
  conversationUuid: string,
  taskId: number
) => {
  const path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/task/${taskId}`
  await fetchWrapper(path, {
    method: 'DELETE'
  })
}
